/**
 * Frontend Redirect Middleware
 *
 * Redirects marketing pages to the new frontend app by doing a full page
 * reload on the same domain. The Cloudflare Worker / ALB routing layer
 * ensures the request lands on the correct application.
 *
 * Marketing pages that should be served by the new frontend:
 * - Homepage (/)
 * - About Us (/about-us)
 * - Pricing (/pricing)
 * - Contact Us (/contact-us)
 * - Business landing (/business)
 * - Personal Affairs (/personal-affairs)
 * - Real Estate (/real-estate)
 * - Estate Planner (/estate-planner)
 * - Careers (/careers)
 * - Affiliate (/affiliate)
 * - Plus (/plus)
 * - Compliance Guard (/compliance-guard)
 * - Registered Agent (/registered-agent)
 * - Business Formation (/business-formation)
 * - Guides (/guides)
 * - Categories (/categories)
 * - Products (/products) - product lander pages
 * - Text pages (/text-page)
 * - Partnerships (/partnerships)
 * - Search (/search)
 */

const MARKETING_ROUTES = new Set([
  '', // homepage
  'about-us',
  'pricing',
  'contact-us',
  'business',
  'personal-affairs',
  'real-estate',
  'estate-planner',
  'careers',
  'affiliate',
  'plus',
  'compliance-guard',
  'registered-agent',
  'business-formation',
  'guides',
  'categories',
  'products',
  'text-page',
  'partnerships',
  'search',
  'privacy-policy',
  'terms-of-use',
  'supplemental-terms-of-service',
  'cookie-policy'
])

export default function ({ route, $config }) {
  const frontendRedirectEnabled = process.env.FRONTEND_REDIRECT_ENABLED === 'true' || $config?.frontendRedirectEnabled

  if (!frontendRedirectEnabled) {
    return
  }

  const path = route.path
  if (!path.startsWith('/') || path.startsWith('//')) {
    return
  }

  const firstSegment = path.split('/')[1] || ''
  const isMarketingRoute = MARKETING_ROUTES.has(firstSegment)

  // The account area is the new dashboard where the routing layer sends
  // /account to the frontend. A client-side navigation into it (the
  // questionnaire's "I'm Done", save-for-later, the reset-password page's
  // "Log In") would otherwise render the legacy account pages inside this
  // SPA and never reach the worker, so make it a full page load. Loop-safe
  // where a path is still served by this app: if the server rendered this
  // visit at exactly the path being navigated to, the routing layer already
  // chose static for it, so stay put. Only that exact path — the
  // reset-password page is itself under /account and used to trip this,
  // leaving the customer on the legacy account page after a reset.
  if (firstSegment === 'account' && process.client && !serverRenderedPath(path)) {
    window.location.href = `${path}${queryString(route)}`
    return
  }

  // Routes currently under A/B test between static (Nuxt 2) and frontend (Nuxt 4).
  // Set via abTestPaths env var (comma-separated path segments) from Terraform.
  const abTestPaths = (process.env.abTestPaths || $config?.abTestPaths || '').split(',').filter(Boolean)
  const isAbTestRoute = abTestPaths.includes(firstSegment)

  // If this route is being A/B tested, don't redirect — the Cloudflare Worker
  // handles routing and the user may be bucketed to static.
  if (isMarketingRoute && isAbTestRoute) {
    return
  }

  if (isMarketingRoute && process.client) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Frontend Redirect] Reloading to frontend app for ${path}`)
    }

    window.location.href = `${path}${queryString(route)}`
  }
}

function queryString (route) {
  return route.query && Object.keys(route.query).length
    ? '?' + new URLSearchParams(route.query).toString()
    : ''
}

function serverRenderedPath (path) {
  const ssrPath = (typeof window !== 'undefined' && window.__NUXT__ && window.__NUXT__.routePath) || ''
  return ssrPath.split('?')[0] === path
}
